import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

const getSentenceTokens = (sentence) => {
  if (!sentence || typeof sentence !== "string") return [];
  return sentence
    .trim()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);
};

const normalizeSentenceValue = (sentence) =>
  getSentenceTokens(sentence).join(" ");

const ReArrangeActivity = ({
  currentActivity,
  submitted,
  shuffledWords,
  setShuffledWords,
  handleOptionChange,
  rearrangeSelections,
  setHasUserInteracted,
}) => {
  const isSubmitted = submitted?.isSubmitted || false;
  const isRtlDirection = currentActivity?.Direction?.toUpperCase() === "RTL";

  // 🔑 Always render from QuizPage-provided shuffledWords
  const displayedWords = isRtlDirection
    ? [...shuffledWords].reverse()
    : shuffledWords;

  // Student answer
  const userSentence = normalizeSentenceValue(
    rearrangeSelections?.[currentActivity?.ContentId]
  );

  // Correct answers
  const correctSentences = Array.isArray(currentActivity?.Options)
    ? currentActivity.Options.map((option) =>
        normalizeSentenceValue(option?.Value)
      ).filter(Boolean)
    : [];

  const isCorrect =
    Boolean(userSentence) &&
    correctSentences.some((sentence) => sentence === userSentence);

  // 🔥 Drag logic (RTL-safe)
  const handleDragEnd = (result) => {
    if (!result.destination || isSubmitted) return;
    setHasUserInteracted(true);
    const updated = Array.from(shuffledWords);

    const realSourceIndex = isRtlDirection
      ? updated.length - 1 - result.source.index
      : result.source.index;

    const realDestIndex = isRtlDirection
      ? updated.length - 1 - result.destination.index
      : result.destination.index;

    const [moved] = updated.splice(realSourceIndex, 1);
    updated.splice(realDestIndex, 0, moved);

    setShuffledWords(updated);

    // Store sentence for validation
    handleOptionChange(currentActivity.ContentId, 1, updated.join(" "));
  };

  const resultBgColor = isSubmitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  return (
    <>
      {/* ---------- DRAG UI ---------- */}
      <Box sx={{ padding: 2, marginTop: 2 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId="rearrange"
            direction="horizontal"
            isDropDisabled={isSubmitted}
          >
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  border: "1px solid #ccc",
                  padding: 2,
                  borderRadius: 2,

                  // react-beautiful-dnd must stay LTR
                  direction: "ltr",
                  textAlign: "left",
                }}
              >
                {displayedWords.map((word, index) => (
                  <Draggable
                    key={`${word}-${index}`}
                    draggableId={`${word}-${index}`}
                    index={index}
                    isDragDisabled={isSubmitted}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        dir={isRtlDirection ? "rtl" : "ltr"}
                        sx={{
                          padding: "8px 16px",
                          fontSize: "1.5rem",
                          fontFamily: "Traditional Arabic, serif",
                          cursor: isSubmitted ? "not-allowed" : "grab",
                          boxShadow: snapshot.isDragging ? 6 : 2,
                          textAlign: isRtlDirection ? "right" : "left",
                        }}
                      >
                        {word}
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      {/* ---------- RESULT + RATIONALE ---------- */}
      {isSubmitted && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: resultBgColor,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            {isCorrect ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            ) : (
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            )}
          </Box>

          <Typography
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              mt: 1,
              color: isCorrect ? "green" : "red",
            }}
          >
            {isCorrect ? "Correct Answer!" : "Incorrect Answer!"}
          </Typography>

          <Box sx={{ mt: 2 }}>
            {currentActivity.Options?.map((opt, idx) => {
              const text = opt.Rationale?.Text?.trim();
              const cid = opt.Rationale?.ContentId;

              return (
                <Box
                  key={idx}
                  sx={{
                    padding: 1,
                    marginTop: 1,
                    border: "1px solid #eee",
                    borderRadius: 2,
                    fontSize: "0.85rem",
                  }}
                >
                  {text ? (
                    <Typography>Rationale: {text}</Typography>
                  ) : cid ? (
                    <Typography>Rationale ID: {cid}</Typography>
                  ) : (
                    <Typography fontStyle="italic">
                      No rationale available.
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ReArrangeActivity;
